import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { ApiResponse } from '../../shared/types';

/**
 * DRB/SiteWatch plan → Way package mapping (CWS-734 Phase 2), consumed by the
 * vendor-membership import writer to resolve which package a migrated member's
 * subscription attaches to. Lives on svc-admin-panel, not svc-vendor-sync —
 * distinct from VendorSyncService, which owns credentials and the sync job itself.
 */

export interface VendorPlanMappingEntry {
  plan_name: string;
  package_id: string;
  /** Null when the mapped package no longer exists (deleted since mapping). */
  package_name: string | null;
  /** Re-validated live on every fetch — a package edited/deleted since mapping shows up as false here. */
  valid: boolean;
  /** Present only when valid is false. One of the reason codes vendorPlanMapping.service.js returns. */
  reason?: string;
}

export interface VendorPackageOption {
  id: string;
  package_name: string;
  wash_count_period: string;
  wash_count_frequency: number;
}

export interface VendorPlanMapping {
  vendor: string;
  /** False until SiteWatch credentials are saved for this car wash — saving a mapping requires them. */
  has_credentials: boolean;
  plan_package_map: VendorPlanMappingEntry[];
  available_packages: VendorPackageOption[];
}

@Injectable({
  providedIn: 'root',
})
export class VendorPlanMappingService {
  private readonly apiService = inject(ApiService);

  private mappingPath(companyId: string, vendor: string): string {
    return `admin/super-admin/companies/${companyId}/vendor-plan-mapping/${vendor}`;
  }

  getPlanMapping(companyId: string, vendor: string): Observable<ApiResponse<VendorPlanMapping>> {
    return this.apiService
      .getWithSuperAdminToken<VendorPlanMapping>(this.mappingPath(companyId, vendor))
      .pipe(catchError(error => throwError(() => error)));
  }

  /**
   * Replace the full plan → package mapping (all-or-nothing — see the backend service).
   * @param planPackageMap plan name -> Way package id
   */
  updatePlanMapping(
    companyId: string,
    vendor: string,
    planPackageMap: Record<string, string>
  ): Observable<ApiResponse<VendorPlanMapping>> {
    return this.apiService
      .putWithSuperAdminToken<VendorPlanMapping>(this.mappingPath(companyId, vendor), {
        plan_package_map: planPackageMap,
      })
      .pipe(catchError(error => throwError(() => error)));
  }
}
